import React, { createContext, useReducer, useContext } from 'react';
import { authenticate, showBlockstackConnect, openContractCall, openContractDeploy, openSTXTransfer } from '@stacks/connect';
export * from '@stacks/connect';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var States;

(function (States) {
  States["UPDATE_AUTH_OPTIONS"] = "data/update-auth-options";
})(States || (States = {}));

var initialState = {
  isOpen: false,
  isAuthenticating: false,
  authData: undefined,
  userSession: undefined,
  authOptions: {
    redirectTo: '',
    manifestPath: '',
    finished: function finished() {
      return null;
    },
    authOrigin: undefined,
    sendToSignIn: false,
    appDetails: {
      name: '',
      icon: ''
    }
  }
};

var connectReducer = function connectReducer(state, _ref) {
  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case States.UPDATE_AUTH_OPTIONS:
      {
        return _extends({}, state, {
          authOptions: _extends({}, state.authOptions, payload)
        });
      }

    default:
      {
        throw new Error("Unhandled action type: " + type);
      }
  }
};

var ConnectContext = /*#__PURE__*/createContext(initialState);
var ConnectDispatchContext = /*#__PURE__*/createContext(undefined);

var ConnectProvider = function ConnectProvider(_ref2) {
  var authOptions = _ref2.authOptions,
      children = _ref2.children;

  var _useReducer = useReducer(connectReducer, initialState),
      state = _useReducer[0],
      dispatch = _useReducer[1];

  return React.createElement(ConnectContext.Provider, {
    value: _extends({}, state, {
      authOptions: authOptions
    })
  }, React.createElement(ConnectDispatchContext.Provider, {
    value: dispatch
  }, children));
};

/**
 * Usage
 *
 *
 * const auth = {
      manifestPath: '/static/manifest.json',
      redirectTo: '/',
      finished: ({userSession}) => {
        doFinishSignIn();
      },
      authOrigin,
      appDetails: {
        name: 'Wink',
        icon: 'url'
      }
    }
 *
 * <Connect authOptions={authOptions} />
 */

var Connect = function Connect(_ref) {
  var authOptions = _ref.authOptions,
      children = _ref.children;
  return React.createElement(ConnectProvider, {
    authOptions: authOptions
  }, children);
};

var useConnectDispatch = function useConnectDispatch() {
  var dispatch = useContext(ConnectDispatchContext);

  if (!dispatch) {
    throw new Error('This must be used within the ConnectProvider component.');
  }

  return dispatch;
};

var useConnect = function useConnect() {
  var _useContext = useContext(ConnectContext),
      isOpen = _useContext.isOpen,
      isAuthenticating = _useContext.isAuthenticating,
      authData = _useContext.authData,
      authOptions = _useContext.authOptions,
      userSession = _useContext.userSession;

  var dispatch = useConnectDispatch();

  var doUpdateAuthOptions = function doUpdateAuthOptions(payload) {
    return dispatch({
      type: States.UPDATE_AUTH_OPTIONS,
      payload: payload
    });
  };

  var doOpenAuth = function doOpenAuth(signIn, opts) {
    if (signIn) {
      var options = _extends({}, authOptions, opts, {
        sendToSignIn: true
      });

      void authenticate(options);
      return;
    } else {
      showBlockstackConnect(_extends({}, authOptions, {
        sendToSignIn: false
      }));
    }

    authOptions && doUpdateAuthOptions(authOptions);
  };

  var doAuth = function doAuth(options) {
    if (options === void 0) {
      options = {};
    }

    void authenticate(_extends({}, authOptions, options));
  };

  var doContractCall = function doContractCall(opts) {
    return openContractCall(_extends({}, opts, {
      authOrigin: authOptions.authOrigin,
      appDetails: authOptions.appDetails
    }));
  };

  var doContractDeploy = function doContractDeploy(opts) {
    return openContractDeploy(_extends({}, opts, {
      authOrigin: authOptions.authOrigin,
      appDetails: authOptions.appDetails
    }));
  };

  var doSTXTransfer = function doSTXTransfer(opts) {
    return openSTXTransfer(_extends({}, opts, {
      authOrigin: authOptions.authOrigin,
      appDetails: authOptions.appDetails
    }));
  };

  return {
    isOpen: isOpen,
    isAuthenticating: isAuthenticating,
    authData: authData,
    authOptions: authOptions,
    userSession: userSession,
    doOpenAuth: doOpenAuth,
    doAuth: doAuth,
    authenticate: authenticate,
    doContractCall: doContractCall,
    doContractDeploy: doContractDeploy,
    doSTXTransfer: doSTXTransfer
  };
};

export { Connect, useConnect };
//# sourceMappingURL=connect-react.esm.js.map
